// VoicePage admin (Sprint 8): configurazione modalità voce.
// Sezioni: stato, STT (Deepgram), TTS (ElevenLabs), limiti audio, privacy alert.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api';
import type { VoiceListItem } from '../../lib/api/settings';
import { FormRow, PrimaryButton, SettingsCard, SuccessHint, inputStyle } from './_shared';

const STT_MODELS = [
  { value: 'nova-3', label: 'Nova-3 (consigliato — multilingua, bassa latenza)' },
  { value: 'nova-2', label: 'Nova-2 (alternativa più economica)' },
];

const TTS_MODELS = [
  { value: 'eleven_multilingual_v2', label: 'Multilingual v2 (consigliato — 29 lingue)' },
  {
    value: 'eleven_flash_v2_5',
    label: 'Flash v2.5 (bassa latenza, qualità leggermente inferiore)',
  },
];

export function VoicePage() {
  const qc = useQueryClient();
  const voiceQuery = useQuery({
    queryKey: ['admin', 'voice'],
    queryFn: () => api.getVoiceSettings(),
  });

  const [enabled, setEnabled] = useState(false);
  const [sttMode, setSttMode] = useState<'managed' | 'client'>('managed');
  const [sttModel, setSttModel] = useState('nova-3');
  const [ttsMode, setTtsMode] = useState<'managed' | 'client'>('managed');
  const [ttsModel, setTtsModel] = useState('eleven_multilingual_v2');
  const [voiceId, setVoiceId] = useState<string | null>(null);
  const [softMin, setSoftMin] = useState<number | ''>('');
  const [hardMin, setHardMin] = useState<number | ''>('');
  const [sttKeyInput, setSttKeyInput] = useState('');
  const [ttsKeyInput, setTtsKeyInput] = useState('');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    if (!voiceQuery.data) return;
    const v = voiceQuery.data.voice;
    setEnabled(v.enabled);
    setSttMode(v.stt.mode);
    setSttModel(v.stt.model);
    setTtsMode(v.tts.mode);
    setTtsModel(v.tts.model);
    setVoiceId(v.tts.voice_id ?? null);
    setSoftMin(v.limits.monthly_audio_minutes_soft ?? '');
    setHardMin(v.limits.monthly_audio_minutes_hard ?? '');
  }, [voiceQuery.data]);

  const sttKeySet = voiceQuery.data?.stt_client_key_set ?? false;
  const ttsKeySet = voiceQuery.data?.tts_client_key_set ?? false;
  // Lista voci recuperata solo se TTS è managed con env key OPPURE client con key salvata
  const canFetchVoices = ttsMode === 'managed' || ttsKeySet;

  const voicesQuery = useQuery({
    queryKey: ['admin', 'voice', 'voices'],
    queryFn: () => api.listVoiceVoices(),
    enabled: canFetchVoices,
    staleTime: 60_000,
  });

  const filteredVoices = useMemo(() => {
    const all = voicesQuery.data?.voices ?? [];
    return [...all].sort((a, b) => a.name.localeCompare(b.name));
  }, [voicesQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      api.updateVoiceSettings({
        enabled,
        stt: { mode: sttMode, model: sttModel },
        tts: { mode: ttsMode, model: ttsModel, voice_id: voiceId },
        limits: {
          monthly_audio_minutes_soft: softMin === '' ? null : Number(softMin),
          monthly_audio_minutes_hard: hardMin === '' ? null : Number(hardMin),
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'voice'] });
      setShowSaved(true);
      setErrMsg(null);
      setTimeout(() => setShowSaved(false), 2500);
    },
    onError: (err) => setErrMsg(err instanceof ApiClientError ? err.payload.error : 'Errore'),
  });

  const setSttKeyMut = useMutation({
    mutationFn: (k: string) => api.setVoiceSttKey(k),
    onSuccess: (data) => {
      setSttKeyInput('');
      setStatusMsg(`✓ STT key validata (${data.latency_ms}ms)`);
      qc.invalidateQueries({ queryKey: ['admin', 'voice'] });
    },
    onError: (err) => setErrMsg(err instanceof ApiClientError ? err.payload.error : 'Errore'),
  });

  const removeSttKeyMut = useMutation({
    mutationFn: () => api.removeVoiceSttKey(),
    onSuccess: () => {
      setSttMode('managed');
      qc.invalidateQueries({ queryKey: ['admin', 'voice'] });
    },
  });

  const setTtsKeyMut = useMutation({
    mutationFn: (k: string) => api.setVoiceTtsKey(k),
    onSuccess: (data) => {
      setTtsKeyInput('');
      setStatusMsg(
        `✓ TTS key validata (${data.latency_ms}ms${data.voices !== undefined ? ', ' + data.voices + ' voci disponibili' : ''})`,
      );
      qc.invalidateQueries({ queryKey: ['admin', 'voice'] });
      qc.invalidateQueries({ queryKey: ['admin', 'voice', 'voices'] });
    },
    onError: (err) => setErrMsg(err instanceof ApiClientError ? err.payload.error : 'Errore'),
  });

  const removeTtsKeyMut = useMutation({
    mutationFn: () => api.removeVoiceTtsKey(),
    onSuccess: () => {
      setTtsMode('managed');
      qc.invalidateQueries({ queryKey: ['admin', 'voice'] });
      qc.invalidateQueries({ queryKey: ['admin', 'voice', 'voices'] });
    },
  });

  if (voiceQuery.isLoading || !voiceQuery.data) {
    return <div style={{ color: 'var(--mute)' }}>Caricamento…</div>;
  }

  return (
    <div className="space-y-6">
      <SettingsCard
        kicker="Voice · stato"
        title="Modalità voce"
        description="Permette agli utenti finali di parlare con Audiq invece di scrivere. La trascrizione passa via Deepgram (USA), la sintesi vocale via ElevenLabs (USA) sotto SCC GDPR. L'audio NON viene archiviato — solo la trascrizione."
        rightSlot={<SuccessHint visible={showSaved} />}
      >
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="mt-1"
          />
          <div>
            <div className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
              Abilita modalità voce per questa installazione
            </div>
            <div className="text-xs" style={{ color: 'var(--mute)' }}>
              Quando attivo, la chat mostra il toggle testo/voce nella schermata pre-chat. Costo
              aggiuntivo stimato: ~5-10x rispetto al solo testo (es. 1000 min ≈ $67/mese). Per
              attivare devi aver scelto una voce TTS qui sotto.
            </div>
          </div>
        </label>
      </SettingsCard>

      {/* STT */}
      <SettingsCard
        kicker="Voice · STT (speech-to-text)"
        title="Trascrizione · Deepgram"
        description="Il discorso dell'utente viene trasmesso a Deepgram per la trascrizione. Modalità managed = chiave Arkenù; client = chiave tua in Deepgram (fattura diretta)."
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormRow label="Modalità">
              <select
                value={sttMode}
                onChange={(e) => setSttMode(e.target.value as 'managed' | 'client')}
                disabled={
                  !sttKeySet && sttMode === 'managed' /* per attivare client serve la key */
                }
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={inputStyle()}
              >
                <option value="managed">Managed (Arkenù)</option>
                <option value="client" disabled={!sttKeySet}>
                  Client (la mia API key)
                </option>
              </select>
            </FormRow>
            <FormRow label="Modello">
              <select
                value={sttModel}
                onChange={(e) => setSttModel(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={inputStyle()}
              >
                {STT_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </FormRow>
          </div>

          {sttKeySet ? (
            <div
              className="rounded-md border-l-4 p-3 text-sm"
              style={{
                borderColor: 'var(--primary)',
                background: 'color-mix(in oklab, var(--primary) 8%, transparent)',
              }}
            >
              <div className="mono">✓ STT key configurata</div>
              <button
                onClick={() => removeSttKeyMut.mutate()}
                disabled={removeSttKeyMut.isPending}
                className="mt-2 text-xs underline"
                style={{ color: 'var(--danger)' }}
              >
                Rimuovi key e torna a managed
              </button>
            </div>
          ) : null}

          <FormRow
            label={sttKeySet ? 'Sostituisci STT key' : 'Inserisci STT key Deepgram'}
            hint="Verifichiamo con un ping a /v1/projects prima di salvare."
          >
            <div className="flex gap-2">
              <input
                type="password"
                placeholder="dg-xxxxx..."
                value={sttKeyInput}
                onChange={(e) => setSttKeyInput(e.target.value)}
                className="mono flex-1 rounded-md border px-3 py-2 text-sm"
                style={inputStyle()}
              />
              <PrimaryButton
                type="button"
                onClick={() => setSttKeyMut.mutate(sttKeyInput)}
                disabled={!sttKeyInput || setSttKeyMut.isPending}
              >
                {setSttKeyMut.isPending ? 'Verifica…' : 'Verifica e salva'}
              </PrimaryButton>
            </div>
          </FormRow>
        </div>
      </SettingsCard>

      {/* TTS */}
      <SettingsCard
        kicker="Voice · TTS (text-to-speech)"
        title="Sintesi vocale · ElevenLabs"
        description="Audiq ti risponde con voce umana sintetizzata da ElevenLabs."
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormRow label="Modalità">
              <select
                value={ttsMode}
                onChange={(e) => setTtsMode(e.target.value as 'managed' | 'client')}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={inputStyle()}
              >
                <option value="managed">Managed (Arkenù)</option>
                <option value="client" disabled={!ttsKeySet}>
                  Client (la mia API key)
                </option>
              </select>
            </FormRow>
            <FormRow label="Modello">
              <select
                value={ttsModel}
                onChange={(e) => setTtsModel(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={inputStyle()}
              >
                {TTS_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </FormRow>
          </div>

          {ttsKeySet ? (
            <div
              className="rounded-md border-l-4 p-3 text-sm"
              style={{
                borderColor: 'var(--primary)',
                background: 'color-mix(in oklab, var(--primary) 8%, transparent)',
              }}
            >
              <div className="mono">✓ TTS key configurata</div>
              <button
                onClick={() => removeTtsKeyMut.mutate()}
                disabled={removeTtsKeyMut.isPending}
                className="mt-2 text-xs underline"
                style={{ color: 'var(--danger)' }}
              >
                Rimuovi key e torna a managed
              </button>
            </div>
          ) : null}

          <FormRow
            label={ttsKeySet ? 'Sostituisci TTS key' : 'Inserisci TTS key ElevenLabs'}
            hint="Verifichiamo con un ping a /v1/voices prima di salvare."
          >
            <div className="flex gap-2">
              <input
                type="password"
                placeholder="..."
                value={ttsKeyInput}
                onChange={(e) => setTtsKeyInput(e.target.value)}
                className="mono flex-1 rounded-md border px-3 py-2 text-sm"
                style={inputStyle()}
              />
              <PrimaryButton
                type="button"
                onClick={() => setTtsKeyMut.mutate(ttsKeyInput)}
                disabled={!ttsKeyInput || setTtsKeyMut.isPending}
              >
                {setTtsKeyMut.isPending ? 'Verifica…' : 'Verifica e salva'}
              </PrimaryButton>
            </div>
          </FormRow>

          <FormRow
            label="Voce TTS"
            hint={
              voicesQuery.isLoading
                ? 'Caricamento voci...'
                : voicesQuery.isError
                  ? 'Errore nel caricamento voci. Configura prima la TTS key.'
                  : 'Scegli la voce con cui Audiq risponde. Le voci ElevenLabs Multilingual supportano IT/EN/AR/ZH/...'
            }
          >
            <VoiceSelector
              voices={filteredVoices}
              selected={voiceId}
              onChange={setVoiceId}
              loading={voicesQuery.isLoading}
            />
          </FormRow>
        </div>
      </SettingsCard>

      {/* Limiti */}
      <SettingsCard
        kicker="Voice · limiti"
        title="Cap minuti audio mensili"
        description="Soft = alert email; hard = blocco nuove richieste audio. Lascia vuoto per nessun limite."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormRow label="Soft cap (minuti audio/mese)">
            <input
              type="number"
              min={0}
              value={softMin}
              onChange={(e) => setSoftMin(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
            />
          </FormRow>
          <FormRow label="Hard cap (minuti audio/mese)">
            <input
              type="number"
              min={0}
              value={hardMin}
              onChange={(e) => setHardMin(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
            />
          </FormRow>
        </div>
        <div className="mt-1 text-xs" style={{ color: 'var(--mute)' }}>
          Stima costo: 1000 min ≈ $7.70 STT + $60 TTS ≈ $67/mese.
        </div>
      </SettingsCard>

      <div className="flex items-center justify-between gap-4">
        {statusMsg && (
          <div className="text-sm" style={{ color: 'var(--primary)' }}>
            {statusMsg}
          </div>
        )}
        {errMsg && (
          <div className="text-sm" style={{ color: 'var(--danger)' }}>
            {errMsg}
          </div>
        )}
        <PrimaryButton onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Salvo…' : 'Salva configurazione voce'}
        </PrimaryButton>
      </div>
    </div>
  );
}

function VoiceSelector({
  voices,
  selected,
  onChange,
  loading,
}: {
  voices: VoiceListItem[];
  selected: string | null;
  onChange: (v: string) => void;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="text-sm" style={{ color: 'var(--mute)' }}>
        Caricamento voci...
      </div>
    );
  }
  if (voices.length === 0) {
    return (
      <div className="text-sm" style={{ color: 'var(--mute)' }}>
        Nessuna voce disponibile. Configura la TTS key sopra.
      </div>
    );
  }
  return (
    <div>
      <select
        value={selected ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border px-3 py-2 text-sm"
        style={inputStyle()}
      >
        <option value="" disabled>
          — seleziona una voce —
        </option>
        {voices.map((v) => (
          <option key={v.voice_id} value={v.voice_id}>
            {v.name}
            {v.language ? ` (${v.language})` : ''}
          </option>
        ))}
      </select>
      {selected ? (
        <div className="mt-2">
          <PreviewVoice voice={voices.find((v) => v.voice_id === selected) ?? null} />
        </div>
      ) : null}
    </div>
  );
}

function PreviewVoice({ voice }: { voice: VoiceListItem | null }) {
  if (!voice?.preview_url) return null;
  return (
    <audio controls src={voice.preview_url} className="h-8 w-full" preload="none">
      Anteprima voce
    </audio>
  );
}
